<template>
  <div class="dialog-mask">
    <div class="dialog-content">
      <button class="dialog-close-button" @click="closeDialog">
        <CloseCircleOutlineIcon :size="22" />
      </button>
      <div class="dialog-image-container">
        <img src="@/assets/img/agent/handshake.webp" class="dialog-image" />
      </div>
      <h3 class="dialog-title">Nolaのエージェント機能とは？</h3>
      <p class="dialog-description">
        Nolaが、作家の皆さまに代わって、作品の可能性を探る機能です。作品をエージェントに提出すると、書籍化やコミック化など、新たな道が広がります。
        下記よりご確認ください🐈‍⬛
      </p>
      <button class="dialog-action-button" @click="handleAgentNavigation">Nolaエージェントに移動する</button>
      <p class="dialog-note">
        ※現在ご執筆中の作品は<span>{{ isSaved ? "保存済みです。" : "保存されます。" }}</span
        ><br />
        安心してページをご移動ください。
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, getCurrentInstance } from "vue";
import CloseCircleOutlineIcon from "icons/CloseCircleOutline.vue";
import store from "@/store";
import router from "@/router";
import { useAgentStore } from "@/stores/agent";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";

const instance = getCurrentInstance();
const agentStore = useAgentStore();
const isSaved = ref(true);
const nolaAnalytics = new NolaAnalytics(instance?.proxy.$gtm!, store);

const closeDialog = () => {
  instance?.proxy.$close(false);
  nolaAnalytics.logButtonEvent(NolaItemId.AgentFeaturePromotionPopupClose);
};

const handleAgentNavigation = () => {
  nolaAnalytics.logButtonEvent(NolaItemId.AgentFeaturePromotionPopupButton);

  // 原稿が保存されている場合は、エージェントイントロ画面に移動する
  if (isSaved.value) {
    router.push({ name: "agent" });
    closeDialog();
    return;
  }

  // 原稿未保存の場合は、isManuscriptUnsavedAgentNavigationフラグを立てて
  // ManuscriptEditor.vueのsave()で原稿保存後にエージェントイントロ画面に移動する
  agentStore.setManuscriptUnsavedAgentNavigation(true);
  closeDialog();
};

onMounted(() => {
  isSaved.value = store.getters["manuscriptModule/isSaved"];
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.dialog-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
  z-index: 1000;
}

.dialog-content {
  background-color: var(--color-background-normal);
  padding: 24px;
  border-radius: var(--radius-md);
  max-width: 334px;
  box-shadow: var(--shadow-md);
  position: relative;
  text-align: center;
  color: var(--color-text-normal);
}

.dialog-close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-low);
}

.dialog-close-button:hover {
  color: var(--color-text-normal);
}

.dialog-image-container {
  margin-top: 16px;
  margin-bottom: 16px;
}

.dialog-image {
  width: 40%;
  height: auto;
  filter: var(--icon-filter-dark);
}

.dialog-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  margin: 8px 0 16px;
  color: var(--color-text-normal);
}

.dialog-description {
  font-size: var(--font-size-sm);
  line-height: 1.6;
  color: var(--color-text-low);
  margin-bottom: 24px;
  text-align: left;
}

.dialog-action-button {
  position: relative;
  display: inline-block;
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  padding: 12px 32px;
  border: none;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  margin-bottom: 5px;

  @include hover-overlay;
}

.dialog-note {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
  line-height: 1.5;
}
</style>
